<template>
  <v-dialog v-model="dialog" persistent max-width="580" :fullscreen="$vuetify.breakpoint.xsOnly"
    content-class="edit-time-dialog">
    <template v-slot:activator="{ on, attrs }">
      <v-btn class="edit-time-trigger" icon v-bind="attrs" v-on="on" aria-label="Edit time entry">
        <v-icon>mdi-pencil-outline</v-icon>
      </v-btn>
    </template>

    <v-card v-if="form" class="edit-time-card">
      <header class="edit-time-header">
        <span class="edit-time-icon"><v-icon>mdi-calendar-clock-outline</v-icon></span>
        <div>
          <small>Shift correction</small>
          <h2>Edit time entry</h2>
          <p>Update the exact start and end of this shift.</p>
        </div>
        <v-btn icon :disabled="loading" @click="close" aria-label="Close edit time">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </header>

      <div class="edit-time-body">
        <section class="edit-time-field is-in">
          <div class="edit-time-label">
            <span><v-icon small>mdi-login</v-icon>Clock in</span>
            <small>Shift start</small>
          </div>
          <v-text-field type="datetime-local" outlined hide-details
            :value="localDateTimeValue(form.clockInTime)"
            @input="updateTime('clockInTime', $event)"
            :error="Boolean(timeError)"
            aria-label="Clock in date and time"></v-text-field>
        </section>

        <div class="edit-time-arrow"><v-icon>mdi-arrow-right</v-icon></div>

        <section class="edit-time-field is-out">
          <div class="edit-time-label">
            <span><v-icon small>mdi-logout</v-icon>Clock out</span>
            <small>Shift end</small>
          </div>
          <v-text-field v-if="form.clockOutTime" type="datetime-local" outlined hide-details
            :value="localDateTimeValue(form.clockOutTime)"
            @input="updateTime('clockOutTime', $event)"
            :error="Boolean(timeError)"
            aria-label="Clock out date and time"></v-text-field>
          <v-btn v-else class="set-clock-out" outlined block @click="setClockOutTime">
            <v-icon left>mdi-clock-plus-outline</v-icon>Set clock-out time
          </v-btn>
        </section>

        <div v-if="timeError" class="edit-time-message is-error">
          <v-icon small>mdi-alert-circle-outline</v-icon>{{ timeError }}
        </div>
        <div v-else class="edit-time-message">
          <v-icon small>mdi-clock-check-outline</v-icon>
          <span>Updated shift duration</span>
          <strong>{{ shiftDuration }}</strong>
        </div>
      </div>

      <footer class="edit-time-actions">
        <v-btn text large :disabled="loading" @click="close">Cancel</v-btn>
        <v-btn color="primary" rounded large :disabled="Boolean(timeError)" :loading="loading" @click="submit">
          <v-icon left>mdi-content-save-outline</v-icon>Save changes
        </v-btn>
      </footer>
    </v-card>
  </v-dialog>
</template>

<script>
import timeClockService from "@/modules/TimeClock/service";

export default {
  props: ["editRow"],
  data() {
    return {
      dialog: false,
      loading: false,
      form: null,
    };
  },
  computed: {
    timeError() {
      if (!this.form || !this.form.clockInTime) return "Enter a clock-in date and time.";
      if (!this.form.clockOutTime) return "";
      const clockIn = new Date(this.form.clockInTime);
      const clockOut = new Date(this.form.clockOutTime);
      if (Number.isNaN(clockIn.getTime()) || Number.isNaN(clockOut.getTime())) {
        return "Enter valid clock-in and clock-out times.";
      }
      return clockOut <= clockIn ? "Clock-out must be later than clock-in." : "";
    },
    shiftDuration() {
      if (!this.form || !this.form.clockOutTime || this.timeError) return "Open shift";
      const seconds = (new Date(this.form.clockOutTime) - new Date(this.form.clockInTime)) / 1000;
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours} h ${minutes} m`;
    },
  },
  watch: {
    dialog(value) {
      if (value && this.editRow) {
        this.form = { ...this.editRow };
      }
    },
  },
  methods: {
    localDateTimeValue(value) {
      return value ? this.$moment(value).format("YYYY-MM-DDTHH:mm") : "";
    },
    updateTime(field, value) {
      this.form[field] = value
        ? this.$moment(value, "YYYY-MM-DDTHH:mm").toISOString()
        : null;
    },
    setClockOutTime() {
      this.form.clockOutTime = this.$moment().toISOString();
    },
    close() {
      if (!this.loading) this.dialog = false;
    },
    submit() {
      if (this.timeError) return;
      this.loading = true;
      const id = this.form.id;
      const payload = { ...this.form };
      if (payload.clockOutTime) {
        payload.status = "clockedOut";
      }
      timeClockService
        .update(id, payload)
        .then(() => {
          this.dialog = false;
          this.$emit("submit");
          this.loading = false;
        })
        .catch((err) => {
          this.$emit("error");
          this.loading = false;
          console.log("err :: ", err);
        });
    },
  },
};
</script>

<style scoped>
.edit-time-trigger {
  color: var(--v-textSecondary-base) !important;
}

.edit-time-card {
  background: var(--v-surface-base) !important;
  color: var(--v-textPrimary-base);
  overflow: hidden;
}

.edit-time-header {
  align-items: flex-start;
  border-bottom: 1px solid var(--v-border-base);
  display: flex;
  gap: 14px;
  padding: 22px 24px 18px;
}

.edit-time-header > div {
  flex: 1;
  min-width: 0;
}

.edit-time-header small {
  color: var(--v-primary-base);
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.edit-time-header h2 {
  color: var(--v-textPrimary-base);
  font-size: 1.35rem;
  margin: 2px 0;
}

.edit-time-header p {
  color: var(--v-textSecondary-base);
  font-size: 0.8rem;
  margin: 0;
}

.edit-time-icon {
  align-items: center;
  background: rgba(66, 133, 244, 0.13);
  border-radius: 13px;
  display: flex;
  height: 46px;
  justify-content: center;
  width: 46px;
}

.edit-time-icon .v-icon {
  color: var(--v-primary-base);
}

.edit-time-body {
  padding: 22px 24px;
}

.edit-time-field {
  background: var(--v-surfaceMuted-base);
  border: 1px solid var(--v-border-base);
  border-radius: 16px;
  padding: 15px;
}

.edit-time-label {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}

.edit-time-label span {
  align-items: center;
  color: var(--v-textPrimary-base);
  display: flex;
  font-size: 0.8rem;
  font-weight: 800;
  gap: 6px;
  text-transform: uppercase;
}

.edit-time-label small {
  color: var(--v-textSecondary-base);
}

.edit-time-field.is-in .edit-time-label .v-icon {
  color: #31c989;
}

.edit-time-field.is-out .edit-time-label .v-icon {
  color: #f36b75;
}

.edit-time-arrow {
  color: var(--v-textSecondary-base);
  padding: 7px;
  text-align: center;
  transform: rotate(90deg);
}

.set-clock-out {
  color: var(--v-textPrimary-base);
}

.edit-time-message {
  align-items: center;
  background: rgba(45, 190, 126, 0.1);
  border-radius: 12px;
  color: #31c989;
  display: flex;
  font-size: 0.78rem;
  gap: 7px;
  margin-top: 14px;
  padding: 11px 13px;
}

.edit-time-message strong {
  margin-left: auto;
}

.edit-time-message .v-icon {
  color: inherit;
}

.edit-time-message.is-error {
  background: rgba(244, 83, 94, 0.11);
  color: #f36b75;
}

.edit-time-actions {
  align-items: center;
  background: var(--v-surfaceMuted-base);
  border-top: 1px solid var(--v-border-base);
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  padding: 14px 24px;
}

@media (max-width: 600px) {
  .edit-time-card {
    min-height: 100vh;
  }

  .edit-time-header {
    padding: 18px 16px;
  }

  .edit-time-body {
    padding: 18px 16px 96px;
  }

  .edit-time-actions {
    bottom: 0;
    left: 0;
    padding: 12px 16px max(12px, env(safe-area-inset-bottom));
    position: fixed;
    right: 0;
  }
}
</style>

<style>
.edit-time-dialog {
  border-radius: 22px;
}

@media (max-width: 600px) {
  .edit-time-dialog {
    border-radius: 0;
  }
}
</style>
